'use client';

import { useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { ErrorBoundary } from '@benzinga/core-ui';
import { menus, NavigationHeader, NavigationHeaderProps } from '@benzinga/navigation-ui';
import { resolveHubNavEnabled } from '../../_libs/site-shell/resolveHubNav';
import { usePageLocale } from './pageLocale';
import { disableOptinMonsterCampaigns, isArticlePath, isAdLightDomHintActive } from '@benzinga/ads-utils';
import { useBenzingaEdge } from '@benzinga/edge';
import { HeaderProps } from '../../../src/entities/header';
import { applyChromeConfig } from '../../_libs/site-shell/getChromeConfig';
import RootHubNav from './RootHubNav';
import '../../styles/header.scss';

export interface RootHeaderProps {
  headerProps: NavigationHeaderProps & HeaderProps;
}

const RootHeader: React.FC<RootHeaderProps> = ({ headerProps }) => {
  const locale = usePageLocale();
  const pathname = usePathname();
  const { adLightEnabled } = useBenzingaEdge();
  const chromeHeaderProps = useMemo(() => applyChromeConfig(pathname, headerProps), [headerProps, pathname]);
  const hideArticleHeaderAd = isArticlePath(pathname) && (adLightEnabled || isAdLightDomHintActive());

  useEffect(() => {
    document.addEventListener('om.Scripts.init', event => {
      if (event && event['detail']) {
        event['detail'].Scripts.enabled.fonts = false;
      }
    });

    disableOptinMonsterCampaigns();
  }, []);

  if (chromeHeaderProps?.hideNavigationBar) {
    return null;
  }

  if (resolveHubNavEnabled(locale)) {
    return <RootHubNav headerProps={chromeHeaderProps} />;
  }

  return (
    <ErrorBoundary name="header">
      <NavigationHeader
        hideBanner={chromeHeaderProps?.hideBanner}
        hideMenuBar={chromeHeaderProps?.hideMenuBar}
        hideQuoteBar={chromeHeaderProps?.hideQuoteBar}
        hideSearchBar={chromeHeaderProps?.hideSearchBar}
        isMainDrawerVisible={chromeHeaderProps?.isMainDrawerVisible}
        initialBreakingsNews={chromeHeaderProps?.breakingNews}
        initialTrendingTickers={chromeHeaderProps?.initialTrendingTickers}
        initialTrendingTopics={chromeHeaderProps?.initialTrendingTopics}
        logoVariant={chromeHeaderProps?.navigationLogoVariant || 'default'}
        marketTickers={chromeHeaderProps?.marketTickers}
        menus={chromeHeaderProps?.menus ?? menus}
        quotes={chromeHeaderProps?.quotes}
        showAboveHeaderBlock={false}
        expandedHeaderDisplayAd={chromeHeaderProps?.expandedHeaderDisplayAd}
        showHeaderDisplayAd={chromeHeaderProps?.showHeaderDisplayAd ?? true}
        showRotatingBanner={
          hideArticleHeaderAd
            ? false
            : typeof chromeHeaderProps?.showRotatingBanner === 'boolean'
              ? chromeHeaderProps.showRotatingBanner
              : false
        }
        sticky={chromeHeaderProps?.sticky}
        variant={chromeHeaderProps?.variant ?? 'default'}
      />
    </ErrorBoundary>
  );
};

export default RootHeader;
